import { makeVar } from '@apollo/client';
import dayjs from 'dayjs';

import StepLabel from 'components/StepLabel/StepLabel';
import { formatDateToSubmit } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';
import { SETTLEMENTS_CADENCE_VALUES } from 'utilities/constants';
import {
  createReactiveState,
  createFundingAccountHandlers,
  createStepHandlers,
  getFundingAccountProperties,
  getStepProperties,
} from 'utilities/stateHelpers';

export const PRECO_STEPS = {
  CHOOSE_OFFER: { key: 'CHOOSE_OFFER', label: 'Choose Offer' },
  FUNDING_ACCOUNT: { key: 'FUNDING_ACCOUNT', label: 'Funding Account' },
  PAYMENT_PLAN_DETAILS: { key: 'PAYMENT_PLAN_DETAILS', label: 'Payment Plan' },
  REVIEW_SCHEDULE: { key: 'REVIEW_SCHEDULE', label: 'Review Schedule' },
  DISCLOSURE: { key: 'DISCLOSURE', label: 'Disclosures' },
  EFTA: { key: 'EFTA', label: 'Authorization' },
};

export const PRECO_ENROLLMENT_TYPES = Object.freeze({
  PAYMENT_PLAN: 'PAYMENT_PLAN',
  FLEXIBLE_PLAN: 'FLEXIBLE_PLAN',
});

const ALL_STEPS = Object.values(PRECO_STEPS);

const getState = () => precoState.stateVar();

const CHOOSE_OFFER_PROGRESS = {
  title: 'Choose an Offer',
  renderPreprocess: () => null,
  renderPostprocess: () => {
    const { offer } = getState();
    const percentage = Math.round(offer.settlementPercentage * 100);
    return (
      <StepLabel
        status="positive"
        title="Offer Selected"
        secondary={`${percentage}% of balance | ${formatDollarsFromCents(offer.settlementBalance)} over ${offer.durationMonths} month(s)`}
      />
    );
  },
};

const FUNDING_ACCOUNT_PROGRESS = {
  title: 'Select a Funding Account',
  renderPreprocess: () => <StepLabel title="Select a Funding Account" />,
  renderPostprocess: () => {
    const { fundingAccount } = getState();
    return (
      <StepLabel
        status="positive"
        title="Funding Account:"
        secondary={fundingAccount.name}
      />
    );
  },
};

const PAYMENT_PLAN_DETAILS_PROGRESS = {
  title: 'Set Payment Plan Details',
  renderPreprocess: () => <StepLabel title="Set Payment Plan Details" />,
  renderPostprocess: () => {
    const { cadence, startDate, schedule } = getState();
    const activePaymentCount = (schedule ?? []).filter(
      (payment) => payment.changeType !== 'delete',
    ).length;
    const paymentCadenceText =
      cadence === SETTLEMENTS_CADENCE_VALUES.ONE_TIME
        ? '1 payment'
        : `${activePaymentCount} ${cadence} payments`;
    return (
      <StepLabel
        status="positive"
        title="Payment Plan Details:"
        secondary={`${paymentCadenceText} starting ${dayjs(startDate).format('L')}`}
      />
    );
  },
};

const REVIEW_SCHEDULE_PROGRESS = {
  title: 'Review Schedule',
  renderPreprocess: () => <StepLabel title="Review Schedule" />,
  renderPostprocess: () => (
    <StepLabel status="positive" title="Schedule reviewed" />
  ),
};

const DISCLOSURE_PROGRESS = {
  title: 'Read Disclosures',
  renderPreprocess: () => <StepLabel title="Read Disclosures" />,
  renderPostprocess: () => (
    <StepLabel status="positive" title="Disclosures reviewed" />
  ),
};

const EFTA_PROGRESS = {
  title: 'Authorization',
  renderPreprocess: () => <StepLabel title="Authorization" />,
  renderPostprocess: () => null,
};

export const PRECO_PAYMENT_PLAN_PROGRESS_STEPS = [
  CHOOSE_OFFER_PROGRESS,
  FUNDING_ACCOUNT_PROGRESS,
  PAYMENT_PLAN_DETAILS_PROGRESS,
  REVIEW_SCHEDULE_PROGRESS,
  DISCLOSURE_PROGRESS,
  EFTA_PROGRESS,
];

export const PRECO_FLEXIBLE_PLAN_PROGRESS_STEPS = [
  CHOOSE_OFFER_PROGRESS,
  DISCLOSURE_PROGRESS,
];

export const getPrecoProgressSteps = (enrollmentType) => {
  switch (enrollmentType) {
    case PRECO_ENROLLMENT_TYPES.FLEXIBLE_PLAN:
      return PRECO_FLEXIBLE_PLAN_PROGRESS_STEPS;
    default:
      return PRECO_PAYMENT_PLAN_PROGRESS_STEPS;
  }
};

const precoState = createReactiveState(() => ({
  ...getStepProperties(PRECO_STEPS.CHOOSE_OFFER),
  ...getFundingAccountProperties(),

  offer: {
    offerId: null,
    customerId: null,
    accountId: null,
    settlementBalance: null,
    originalOutstandingBalance: null,
    remainingBalance: null,
    expirationDate: null,
    offerState: null,
    terminationReason: null,
    dscName: null,
    offerTermsLabel: null,
    paymentPlanId: null,
    payments: [],
    // Only set when the agent picks from the offer catalog; an offer that is
    // already enrolled carries an expirationDate instead.
    durationMonths: null,
    settlementPercentage: null,
  },

  enrollmentType: null,
  cadence: null,
  startDate: null,
  durationMonths: null,
  schedule: [],
}));

export const usePrecoState = precoState.useState;
export const resetPrecoState = precoState.resetState;
export const precoStateVar = precoState.stateVar;

const stepHandlers = createStepHandlers(precoState, ALL_STEPS);
export const { setNextStep } = stepHandlers;

export const goToPreviousStep = () => {
  if (precoState.stateVar().steps.length === 2) {
    resetPrecoState();
  } else {
    stepHandlers.goToPreviousStep();
  }
};

const { setFundingAccount } = createFundingAccountHandlers(precoState);
export { setFundingAccount };

export const setSelectedOffer = (offer, outstandingBalance, enrollmentType) => {
  const expirationDate = formatDateToSubmit(
    dayjs().add(offer.durationMonths, 'month'),
  );

  precoState.setPropertyValues({
    offer: {
      ...precoState.stateVar().offer,
      settlementBalance: offer.settlementBalance,
      originalOutstandingBalance: outstandingBalance,
      expirationDate,
      durationMonths: offer.durationMonths,
      settlementPercentage: offer.settlementPercentage,
    },
    enrollmentType,
  });
};

const mapOfferData = (data, payments = []) => ({
  offerId: data.offerId,
  customerId: data.customerId,
  accountId: data.accountId,
  settlementBalance: data.settlementBalance,
  originalOutstandingBalance: data.originalOutstandingBalance,
  remainingBalance: data.remainingBalance,
  expirationDate: data.expirationDate,
  offerState: data.offerState,
  terminationReason: data.terminationReason ?? null,
  dscName: data.dscName ?? null,
  offerTermsLabel: data.offerTermsLabel ?? null,
  paymentPlanId: data.paymentPlanId ?? null,
  payments,
});

export const setEnrolledOffer = (enrollmentResponse) => {
  const { data } = enrollmentResponse;
  precoState.setPropertyValue('offer', mapOfferData(data));
};

export const hydrateOffer = (queryResponse) => {
  const { data } = queryResponse;
  precoState.setPropertyValue('offer', mapOfferData(data, data.payments ?? []));
};

export const setPrecoSchedule = (schedule) => {
  precoState.setPropertyValue('schedule', schedule);
};

export const setPrecoPaymentDetails = ({
  cadence,
  startDate,
  durationMonths,
}) => {
  precoState.setPropertyValues({ cadence, startDate, durationMonths });
};

export const buildEnrollVariables = ({ offer, customerId, accountId }) => ({
  customerId,
  accountId,
  settlementBalance: offer.settlementBalance,
  originalOutstandingBalance: offer.originalOutstandingBalance,
  expirationDate: formatDateToSubmit(offer.expirationDate),
  dscName: offer.dscName ?? null,
  offerTermsLabel: offer.offerTermsLabel ?? null,
});

const NO_PLAN_CREATION_ERROR = { offerId: null, message: null };

// Kept out of precoState because exiting the wizard calls resetPrecoState(), and the
// failure has to outlive that so the offer details view can explain the missing plan.
export const precoPlanCreationErrorVar = makeVar(NO_PLAN_CREATION_ERROR);

export const clearPrecoPlanCreationError = () =>
  precoPlanCreationErrorVar(NO_PLAN_CREATION_ERROR);
